/**
 * TargetManager
 * 
 * Manages anchor registration and position tracking for UI elements.
 * Used by the FlowDirector to resolve target references and track element positions.
 */

import React from 'react';
import { ITargetManager, AnchorInfo, PositionChangeCallback } from '../types';
import { TargetRef } from '../types';

export class TargetManager implements ITargetManager {
  private anchors: Map<string, React.RefObject<HTMLElement | null>> = new Map();
  private positions: Map<string, DOMRect> = new Map();
  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private positionCallbacks: Set<PositionChangeCallback> = new Set();
  private rafId: number | null = null;
  private dirty: boolean = false;

  constructor() {
    this.initializeObservers();
  }

  /**
   * Initialize observers for tracking position changes
   */
  private initializeObservers(): void {
    // ResizeObserver for element size changes
    this.resizeObserver = new ResizeObserver(() => {
      this.markDirty();
    });

    // MutationObserver for DOM changes
    this.mutationObserver = new MutationObserver(() => {
      this.markDirty();
    });

    // Observe body for structural changes
    if (typeof document !== 'undefined') {
      this.mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Listen for scroll events
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });
    }
  }

  /**
   * Handle scroll events
   */
  private handleScroll = (): void => {
    this.markDirty();
  };

  /**
   * Handle resize events
   */
  private handleResize = (): void => {
    this.markDirty();
  };

  /**
   * Mark positions as dirty and schedule update
   */
  private markDirty(): void {
    if (this.dirty) return;
    this.dirty = true;
    
    if (this.rafId === null) {
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.dirty = false;
        this.updatePositions();
      });
    }
  }

  /**
   * Register an anchor element
   */
  registerAnchor(id: string, ref: React.RefObject<HTMLElement | null>): void {
    this.anchors.set(id, ref);
    
    // Observe the element for size changes
    if (ref.current && this.resizeObserver) {
      this.resizeObserver.observe(ref.current);
    }
    
    // Update position immediately
    this.updateAnchorPosition(id);
  }

  /**
   * Unregister an anchor element
   */
  unregisterAnchor(id: string): void {
    const ref = this.anchors.get(id);
    if (ref?.current && this.resizeObserver) {
      this.resizeObserver.unobserve(ref.current);
    }
    
    this.anchors.delete(id);
    this.positions.delete(id);
  }

  /**
   * Update position for a single anchor
   */
  private updateAnchorPosition(id: string): void {
    const ref = this.anchors.get(id);
    if (!ref?.current) return;
    
    const rect = ref.current.getBoundingClientRect();
    const oldRect = this.positions.get(id);
    
    // Check if position changed
    if (oldRect && this.rectsEqual(oldRect, rect)) {
      return;
    }
    
    this.positions.set(id, rect);
    
    // Notify callbacks
    this.positionCallbacks.forEach((callback) => {
      callback(id, rect);
    });
  }

  /**
   * Update all anchor positions
   */
  private updatePositions(): void {
    this.anchors.forEach((_, id) => {
      this.updateAnchorPosition(id);
    });
  }

  /**
   * Force refresh of all positions
   */
  refreshPositions(): void {
    this.updatePositions();
  }

  /**
   * Check if two DOMRects are equal
   */
  private rectsEqual(a: DOMRect, b: DOMRect): boolean {
    return (
      a.x === b.x &&
      a.y === b.y &&
      a.width === b.width &&
      a.height === b.height
    );
  }

  /**
   * Resolve a target reference to a DOMRect
   */
  resolve(target: TargetRef): DOMRect | null {
    switch (target.type) {
      case 'anchor':
        return this.resolveAnchor(target.id);
        
      case 'selector':
        return this.resolveSelector(target.selector);
        
      case 'viewport':
        return this.resolveViewport(target.x, target.y);
        
      default:
        return null;
    }
  }

  /**
   * Resolve an anchor by ID
   */
  private resolveAnchor(id: string): DOMRect | null {
    // Return cached position
    const cached = this.positions.get(id);
    if (cached) return cached;
    
    // Try to get fresh position
    const ref = this.anchors.get(id);
    if (ref?.current) {
      const rect = ref.current.getBoundingClientRect();
      this.positions.set(id, rect);
      return rect;
    }
    
    return null;
  }

  /**
   * Resolve a CSS selector to a DOMRect
   */
  private resolveSelector(selector: string): DOMRect | null {
    try {
      const element = document.querySelector(selector);
      if (element) {
        return element.getBoundingClientRect();
      }
    } catch {
      console.warn(`[TargetManager] Invalid selector: ${selector}`);
    }
    return null;
  }

  /**
   * Resolve viewport-relative coordinates to a DOMRect
   */
  private resolveViewport(x: number, y: number): DOMRect {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // x and y are 0-1 values
    const posX = x * viewportWidth;
    const posY = y * viewportHeight;
    
    // Return a small rect at that position
    return new DOMRect(posX, posY, 0, 0);
  }

  /**
   * Get anchor info by ID
   */
  getAnchor(id: string): AnchorInfo | null {
    const ref = this.anchors.get(id);
    if (!ref?.current) return null;
    
    const rect = this.positions.get(id) ?? ref.current.getBoundingClientRect();
    
    return {
      id,
      rect,
      element: ref.current,
    };
  }

  /**
   * Subscribe to position changes
   */
  onPositionChange(callback: PositionChangeCallback): () => void {
    this.positionCallbacks.add(callback);
    
    return () => {
      this.positionCallbacks.delete(callback);
    };
  }

  /**
   * Wait for an element to be present
   */
  async waitForElement(target: TargetRef, timeoutMs: number = 5000): Promise<DOMRect | null> {
    const startTime = Date.now();
    
    return new Promise((resolve) => {
      const check = () => {
        const rect = this.resolve(target);
        if (rect) {
          resolve(rect);
          return;
        }
        
        if (Date.now() - startTime > timeoutMs) {
          resolve(null);
          return;
        }
        
        requestAnimationFrame(check);
      };
      
      check();
    });
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
    }
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
    }
    
    this.anchors.clear();
    this.positions.clear();
    this.positionCallbacks.clear();
  }
}

export default TargetManager;
